import { StyledCta } from 'components/marketingSite/shared/Cta.styled';
import { Heading1 } from 'components/shared/Typography';
import styled from 'styled-components';
import {
  color,
  container,
  fontFamily,
  fontSize,
  fontWeight,
  radius,
  screen,
  shadow,
  spacing
} from '../../../theme';

export const StyledContainer = styled.div`
  position: relative;
`;

export const StyledPost = styled.article`
  padding: ${spacing('xl4')} ${spacing('md')};
  max-width: ${container('xl')};
  margin: auto;
  width: 100%;

  ${StyledCta} {
    margin-top: ${spacing('xl5')};
  }
`;

export const StyledPostHeader = styled.header`
  text-align: center;
  margin-bottom: ${spacing('xl3')};

  ${Heading1} {
    margin-bottom: ${spacing('xs')};
    text-align: left;

    &:before {
      left: 0;
      transform: unset;
    }

    @media (min-width: ${screen('md')}) {
      text-align: center;

      &:before {
        left: 50%;
        transform: translateX(-50%);
      }
    }
  }
`;

export const StyledPostInfo = styled.div`
  color: ${color('text_subtle')};
  display: flex;
  justify-content: left;
  align-items: center;
  gap: ${spacing('sm')};

  @media (min-width: ${screen('md')}) {
    justify-content: center;
  }
`;

export const StyledPostOneLang = styled.p`
  text-align: center;

  span {
    display: inline-block;
    color: ${color('midnight_200')};
    background-color: ${color('midnight_900')};
    padding: ${spacing('xs')};
    font-size: ${fontSize('xs')};
    font-weight: ${fontWeight('extraBold')};
  }
`;

export const StyledPostInfoFooter = styled.div`
  max-width: ${container('md')};
  margin: ${spacing('xl4')} auto ${spacing('md')} auto;
`;

export const StyledPostHero = styled.img`
  width: 100%;
  display: block;
  margin-bottom: ${spacing('xl3')};
`;

export const StyledPostContent = styled.div`
  padding: 0 ${spacing('md')};
  font-size: ${fontSize('md')};
  line-height: 1.6;

  @media (min-width: ${screen('lg')}) {
    font-size: ${fontSize('lg')};
  }

  p,
  ul,
  ol,
  h2,
  h3,
  h4,
  h5,
  h6,
  img,
  blockquote {
    max-width: ${container('md')};
    margin-left: auto;
    margin-right: auto;
  }

  img {
    display: block;
    margin-top: ${spacing('xl')};
    margin-bottom: ${spacing('xl')};
    border: ${spacing('md')} solid ${color('text_default')};
    border-radius: ${radius('md')};
    width: 100%;
  }

  h2 {
    font-family: ${fontFamily('serif')};
    font-weight: ${fontWeight('black')};
    font-size: ${fontSize('xl2')};
    line-height: 1.17;
    margin-top: ${spacing('xl4')};
    margin-bottom: ${spacing('xl')};

    @media (min-width: ${screen('md')}) {
      font-size: ${fontSize('xl3')};
    }

    @media (min-width: ${screen('lg')}) {
      font-size: ${fontSize('xl4')};
    }
  }

  h3 {
    font-family: ${fontFamily('serif')};
    font-weight: ${fontWeight('black')};
    font-size: ${fontSize('xl')};
    line-height: 1.12;
    margin-top: ${spacing('xl4')};
    margin-bottom: ${spacing('lg')};

    @media (min-width: ${screen('md')}) {
      font-size: ${fontSize('xl2')};
    }

    @media (min-width: ${screen('lg')}) {
      font-size: ${fontSize('xl3')};
    }
  }

  h4 {
    font-family: ${fontFamily('serif')};
    font-weight: ${fontWeight('bold')};
    font-size: ${fontSize('lg')};
    line-height: 1.14;
    margin-top: ${spacing('xl3')};
    margin-bottom: ${spacing('md')};

    @media (min-width: ${screen('md')}) {
      font-size: ${fontSize('xl')};
    }

    @media (min-width: ${screen('lg')}) {
      font-size: ${fontSize('xl2')};
    }
  }

  h5 {
    font-family: ${fontFamily('serif')};
    font-weight: ${fontWeight('bold')};
    font-size: ${fontSize('md')};
    line-height: 1.14;
    margin-top: ${spacing('xl')};
    margin-bottom: ${spacing('md')};

    @media (min-width: ${screen('md')}) {
      font-size: ${fontSize('lg')};
    }

    @media (min-width: ${screen('lg')}) {
      font-size: ${fontSize('xl')};
    }
  }

  h6 {
    font-family: ${fontFamily('serif')};
    font-weight: ${fontWeight('bold')};
    font-size: ${fontSize('sm')};
    line-height: 1.14;
    margin-top: ${spacing('xl')};
    margin-bottom: ${spacing('sm')};

    @media (min-width: ${screen('md')}) {
      font-size: ${fontSize('md')};
    }

    @media (min-width: ${screen('lg')}) {
      font-size: ${fontSize('lg')};
    }
  }

  ul {
    list-style-type: disc;
    padding-left: 1em;
  }

  ol {
    padding-left: 1em;
  }

  li {
    margin-top: ${spacing('md')};
    margin-bottom: ${spacing('md')};
    list-style: inherit;
  }

  table {
    max-width: ${container('xl')};
    font-size: ${fontSize('sm')};
    border-radius: ${radius('md')};
    box-shadow: ${shadow('md')};
    display: block;
    overflow-x: auto;
  }

  tbody {
    width: 100%;
    display: table;
  }

  tr:nth-child(1n) {
    background: ${color('white')};
  }

  td {
    padding: ${spacing('md')} ${spacing('lg')};
    text-align: left;
    white-space: nowrap;

    &.has-text-align-center {
      text-align: center;
    }
  }

  .wp-block-table {
    margin: ${spacing('xl')} 0;
    overflow-x: auto;
    padding: ${spacing('md')} ${spacing('xs')};

    table {
      border-collapse: collapse;
      width: 100%;
    }

    tr:nth-child(odd) {
      background: ${color('midnight_100')};
    }
  }

  .wp-block-image,
  .wp-caption {
    max-width: ${container('md')};
    margin: ${spacing('xl')} auto;
    display: block;
    border: ${spacing('md')} solid ${color('text_default')};
    border-radius: ${radius('md')};
    background-color: ${color('text_default')};
    color: ${color('text_default_inverse')};
    font-size: ${fontSize('sm')};

    .size-medium {
      max-width: ${container('sm')};
    }

    img {
      border: 0;
      margin: 0;
      border-radius: 0;
    }

    figcaption {
      text-align: left;
      padding-top: ${spacing('md')};
    }

    a {
      color: ${color('link_inverse')};

      &:hover {
        color: ${color('link_inverse_hover')};
      }

      &:active {
        color: ${color('link_inverse_active')};
      }
    }
  }

  .wp-block-group {
    max-width: ${container('md')};
    margin: ${spacing('xl')} auto;
    display: block;

    iframe {
      border: 0;
    }
  }

  .wp-block-video {
    max-width: ${container('md')};
    margin: ${spacing('xl')} auto;
    border: ${spacing('md')} solid ${color('text_default')};
    border-radius: ${radius('md')};

    video {
      width: 100%;
    }
  }

  .wp-block-embed-youtube {
    max-width: ${container('md')};
    margin: ${spacing('xl')} auto;
    border: ${spacing('md')} solid ${color('text_default')};
    border-radius: ${radius('md')};

    .wp-block-embed__wrapper {
      position: relative;
      padding-bottom: 56.25%;
      height: 0;
      overflow: hidden;
      max-width: 100%;
    }

    iframe {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
    }
  }

  .wp-block-embed-arcade {
    max-width: ${container('md')};
    margin: ${spacing('xl')} auto;
    border: ${spacing('md')} solid ${color('text_default')};
    border-radius: ${radius('md')};
    background-color: ${color('text_default')};

    .wp-block-embed__wrapper {
      position: relative;
      padding-bottom: 52.75%;
      height: 0;
      overflow: hidden;
      max-width: 100%;
      background-color: ${color('text_default')};

      @media (max-width: ${screen('mdMax')}) {
        padding-bottom: 62%;
      }
    }

    iframe {
      border: 0;
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
    }
  }

  .wp-block-spacer {
    display: none;
  }

  .has-inline-color {
    color: inherit !important;
  }

  *[style*='color'] {
    color: inherit !important;
  }

  .wp-block-quote,
  .twitter-tweet {
    margin-top: ${spacing('xl')};
    margin-bottom: ${spacing('xl')};
    padding-left: ${spacing('md')};
    border-left: ${spacing('xs2')} solid ${color('emerald_400')};
  }

  .instagram-media {
    margin: ${spacing('xl')} auto !important;
  }

  .wp-block-buttons {
    &.is-layout-flex {
      display: flex;
      align-items: center;
    }
    &.is-content-justification-center {
      justify-content: center;
    }
  }

  .wp-block-button {
    margin-top: ${spacing('xl3')};

    &__link {
      background-color: ${color('emerald_600')} !important;
      color: ${color('white')} !important;
      font-size: ${fontSize('sm')} !important;
      text-transform: uppercase !important;
      font-weight: ${fontWeight('black')} !important;
      padding: ${spacing('md')} ${spacing('lg')} !important;
      border-radius: ${radius('md')} !important;
      transition: 0.3s !important;
      display: inline-block;
      text-align: center;

      &:hover,
      &:active {
        background-color: ${color('emerald_400')} !important;
      }

      &:focus {
        outline: 0;
        box-shadow: 0 0 0 4px ${color('midnight_150')};
        color: ${color('midnight_150')} !important;
      }
    }
  }
`;

export const StyledPostTags = styled.div`
  max-width: ${container('md')};
  margin: ${spacing('xl4')} auto;
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing('xs')};
  align-items: center;
`;

export const StyledRecommendations = styled.section`
  max-width: ${container('xl')};
  margin: ${spacing('xl4')} auto;
`;

export const StyledComments = styled.section`
  max-width: ${container('sm')};
  margin: ${spacing('xl4')} auto;
`;

export const StyledPostNav = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: ${spacing('xl')};

  @media (max-width: ${screen('mdMax')}) {
    justify-content: center;
    gap: ${spacing('md')};
  }
`;

export const StyledPostNavItemButton = styled.span`
  width: 2.5rem;
  height: 2.5rem;
  background: transparent;
  border: 0;
  padding: 0;
  color: ${color('midnight_800')};
  transition: 0.3s;

  svg {
    width: 2.5rem !important;
    height: 2.5rem;
  }
`;

export const StyledPostNavHint = styled.span`
  text-transform: uppercase;
  font-size: ${fontSize('xs')};
  font-weight: ${fontWeight('black')};
`;

export const StyledPostNavItemTop = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing('sm')};
  margin-bottom: ${spacing('md')};
`;

export const StyledPostNavItem = styled.a`
  color: inherit;
  background-color: ${color('white')};
  padding: ${spacing('md')};
  border-radius: ${radius('md')};
  transition: 0.3s;
  width: 18rem;
  font-weight: ${fontWeight('black')};
  display: flex;
  flex-direction: column;
  min-height: 100%;

  &.next {
    text-align: right;
    align-items: flex-end;
  }

  &:hover {
    background-color: ${color('midnight_150')};
    color: inherit;
  }
`;

export const StyledFloatingBannerContainer = styled.div`
  max-width: 20.5rem;
  position: sticky;
  left: calc(50% + ${container('md')} / 2 + ${spacing('xl')});
  bottom: ${spacing('md')};
  margin-bottom: ${spacing('md')};
  margin-right: ${spacing('md')};

  @media (max-width: ${screen('mdMax')}) {
    display: none;
  }
`;
